import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiX, FiLink } from 'react-icons/fi';

const DependencyPicker = ({ dependencies = [], onChange, token, currentTaskId, boardId }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [availableTasks, setAvailableTasks] = useState([]);
  const [selectedIds, setSelectedIds] = useState(dependencies);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && boardId) {
      fetchBoardTasks();
    }
  }, [isOpen, boardId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchBoardTasks = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/boards/${boardId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        const tasks = [];
        
        // Flatten all tasks from all groups
        (data.board.groups || []).forEach(group => {
          (group.items || []).forEach(item => {
            // Don't include current task or already dependent tasks
            if (item.id !== currentTaskId) {
              tasks.push({
                ...item,
                groupName: group.name
              });
            }
          });
        });
        
        setAvailableTasks(tasks);
      }
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    }
  };

  const toggleTask = (taskId) => {
    const newSelection = selectedIds.includes(taskId)
      ? selectedIds.filter(id => id !== taskId)
      : [...selectedIds, taskId];
    setSelectedIds(newSelection);
    onChange(newSelection);
  };

  const getDependentTasks = () => {
    return availableTasks.filter(t => selectedIds.includes(t.id));
  };

  const removeDependency = (taskId) => {
    const newSelection = selectedIds.filter(id => id !== taskId);
    setSelectedIds(newSelection);
    onChange(newSelection);
  };

  return (
    <div style={{ width: '100%' }}>
      {selectedIds.length > 0 && (
        <div style={{ marginBottom: '12px' }}>
          {getDependentTasks().map((task) => (
            <div
              key={task.id}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '6px 12px',
                backgroundColor: 'var(--gray-50)',
                border: '1px solid var(--border-light)',
                borderRadius: '6px',
                marginRight: '8px',
                marginBottom: '8px',
                fontSize: '13px'
              }}
            >
              <FiLink size={12} />
              <span>{task.name}</span>
              <button
                type="button"
                onClick={() => removeDependency(task.id)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--text-secondary)',
                  cursor: 'pointer',
                  padding: '2px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <FiX size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      
      <div ref={dropdownRef} style={{ position: 'relative' }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="dependency-picker-button"
          style={{ width: '100%' }}
        >
          <FiPlus size={14} />
          <span>Add Dependency</span>
        </button>
        {isOpen && (
          <div className="dropdown" style={{ top: '100%', left: 0, marginTop: '4px', minWidth: '300px', maxHeight: '300px', overflowY: 'auto' }}>
            <div style={{ padding: '8px 12px', fontWeight: 600, fontSize: '13px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--border-light)' }}>
              Select Tasks
            </div>
            {availableTasks.length === 0 ? (
              <div style={{ padding: '16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '13px' }}>
                No other tasks available
              </div>
            ) : (
              availableTasks.map((task) => (
                <div
                  key={task.id}
                  className="dropdown-item"
                  onClick={() => toggleTask(task.id)}
                  data-testid="dependency-option"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(task.id)}
                    onChange={() => {}}
                    style={{ marginRight: '8px' }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '14px', fontWeight: 500, marginBottom: '2px' }}>{task.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {task.groupName} • {task.status}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DependencyPicker;